import { Component, EventEmitter, Input, Output, Type } from '@angular/core';
import { GridRowModelTypes, TextButtonComponent } from '@ra-web-tech-ui-toolkit/components';
import { AgGridAngular } from 'ag-grid-angular';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  FilterChangedEvent,
  FilterModel,
  FirstDataRenderedEvent,
  GridOptions,
  GridReadyEvent,
  Module,
  RowClickedEvent,
  RowDataUpdatedEvent,
  RowSelectedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ClipboardModule,
  ExcelExportModule,
  ServerSideRowModelModule,
  SetFilterModule,
  TreeDataModule,
} from 'ag-grid-enterprise';

import { AgCustomNoRowsOverlayComponent } from '../ag-renderers/ag-custom-no-rows-overlay/ag-custom-no-rows-overlay.component';
import { AgToolbarGridLifecycle } from '../ag-toolbar-grid.lifecycle';
import { processCellForClipboard } from '../grid.utils';

@Component({
  selector: 'app-ag-toolbar-grid',
  templateUrl: './ag-toolbar-grid.component.html',
  styleUrl: './ag-toolbar-grid.component.scss',
  imports: [TextButtonComponent, AgGridAngular],
})
export class AgToolbarGridComponent extends AgToolbarGridLifecycle<FilterModel> {
  @Input() noRowsOverlayContent: string = '';
  @Input() noRowsOverlayComponent?: Type<unknown>;
  @Input() isSortFilterApplied?: boolean;
  @Output() firstDataRendered = new EventEmitter<any>();
  @Output() rowDataUpdated = new EventEmitter<any>();
  @Output() rowSelected = new EventEmitter<RowSelectedEvent>();
  @Output() rowClicked = new EventEmitter<RowClickedEvent>();
  @Output() sortChanged = new EventEmitter<SortChangedEvent>();
  @Output() filterChanged = new EventEmitter<FilterChangedEvent>();
  @Input() hideClearFilter?: boolean = false;
  @Input() masterDetail?: boolean = false;
  @Input() detailRowAutoHeight?: boolean = false;
  @Input() detailCellRenderer?: any;
  @Input() detailCellRendererParams?: any;

  readonly DEFAULT_GRID_OPTIONS: GridOptions = {
    animateRows: true,
    domLayout: 'autoHeight',
    cellSelection: true,
    pagination: true,
    rowSelection: { mode: 'singleRow', checkboxes: false, enableClickSelection: false },
  };

  disableClearFilter: boolean = false;

  protected createApplicationGridOptions(): GridOptions {
    const defaultColDef = {
      minWidth: 100,
      autoHeight: true,
      filter: false,
      flex: 1,
      resizable: true,
      unSortIcon: true,
    };
    const getRowHeight = (): number => 60;
    const loadingTemplate = '<span class="ag-overlay-loading-center">Data is loading...</span>';
    const noRowsOverlayComponentParams: any = {
      title: this.noRowsOverlayMsg,
      content: this.noRowsOverlayContent,
    };
    const gridOptions: GridOptions = {
      columnDefs: this.columnDefs,
      defaultColDef: defaultColDef,
      getRowHeight: getRowHeight,
      noRowsOverlayComponentParams: noRowsOverlayComponentParams,
      overlayLoadingTemplate: loadingTemplate,
      paginationPageSize: this.pageSize,
      paginationPageSizeSelector: false,
      processCellForClipboard,
    };

    gridOptions.noRowsOverlayComponent = this.noRowsOverlayComponent ?? AgCustomNoRowsOverlayComponent;

    return gridOptions;
  }

  protected configureRowModel(): void {
    if (this.dataProvider) {
      this.gridOptions.rowModelType = 'serverSide';
      this.gridOptions.cacheBlockSize = this.pageSize;
      this.getRowId ??= (params): string => params.data.id;
    } else {
      this.gridOptions.rowModelType = GridRowModelTypes.ClientSide;
    }
  }

  protected shouldRefreshGrid(refresh: boolean | undefined): boolean {
    return refresh === true;
  }

  protected refreshGrid(): void {
    if (!this.gridApi || this.gridApi.isDestroyed()) {
      return;
    }
    if (this.dataProvider) {
      this.gridApi.refreshServerSide({ purge: true });
      return;
    }
    this.gridApi.refreshCells();
  }

  get isSortFilterAppliedImpl(): boolean {
    return (
      this.isSortFilterApplied ??
      (this.gridApi?.isAnyFilterPresent() ||
        this.gridApi?.getColumnState().some((cs) => {
          const defaulColDef = this.columnDefs.find((cd) => cd.field === cs.colId);
          return cs.sort && cs.sort !== defaulColDef?.initialSort;
        }) ||
        false)
    );
  }

  setClearFilter(): void {
    this.disableClearFilter = !this.isSortFilterAppliedImpl;
  }

  modules: Module[] = [
    ServerSideRowModelModule,
    CellSelectionModule,
    SetFilterModule,
    ClipboardModule,
    ClientSideRowModelModule,
    AllCommunityModule,
    ExcelExportModule,
    TreeDataModule,
  ];

  protected prepareGridReady(_event: GridReadyEvent): void {
    if (this.rowData$ || this.dataProvider) {
      this.gridApi?.setGridOption('loading', true);
    }
    this.gridApi.setFilterModel(this.defaultFilter ?? null);

    if (this.dataProvider) {
      this.gridApi?.setGridOption('serverSideDatasource', this.dataProvider());
    }
  }

  protected applyRowData(event: GridReadyEvent, data: unknown): void {
    event.api.updateGridOptions({
      rowData: data as object[],
      loading: false,
    });
  }

  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    this.gridApi = event.api;
    this.gridApi?.setGridOption('loading', false);
    this.firstDataRendered.emit(event);
  }

  // https://stackoverflow.com/questions/59053076/function-rowselected-has-been-called-twiceselected-and-deselected
  // https://www.ag-grid.com/javascript-data-grid/row-events/#reference-rowNodeEvents-rowSelected
  onRowSelected(event: RowSelectedEvent): void {
    if (!event.node.isSelected()) {
      return;
    }
    this.gridApi = event.api;
    this.rowSelected.emit(event);
  }

  onRowClicked(event: RowClickedEvent): void {
    this.gridApi = event.api;
    this.rowClicked.emit(event);
  }

  onSortChanged(event: SortChangedEvent): void {
    this.setClearFilter();
    this.sortChanged.emit(event);
  }

  onFilterChanged(event: FilterChangedEvent): void {
    this.setClearFilter();
    this.filterChanged.emit(event);
  }

  onRowDataUpdated(event: RowDataUpdatedEvent): void {
    this.gridApi = event.api;
    this.rowDataUpdated.emit(event);
  }

  onModelUpdated(): void {
    if (this.gridApi && !this.gridApi.isDestroyed()) {
      this.gridApi.setGridOption('loading', false);
    }
  }
}
